import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTriageBatch, useCheckTriageItem, useUpdateTriageItem } from '../hooks/useTriage';
import { StatusBadge } from '../components/StatusBadge';
import type { TriageItem } from '../types';

const QUEUE = 'fulfillment-sync-failure';

const METRIC_LABELS: Record<string, { label: string; className: string }> = {
  oms_tracking_update_failure: {
    label: 'OMS Tracking Update',
    className: 'bg-purple-100 text-purple-800',
  },
  ship_memo_bleckmann_transmission_failure: {
    label: 'Bleckmann Transmission',
    className: 'bg-amber-100 text-amber-800',
  },
  ship_memo_ups_transmission_failure: {
    label: 'UPS Transmission',
    className: 'bg-orange-100 text-orange-800',
  },
  shopify_tracking_update_failure: {
    label: 'Shopify Tracking',
    className: 'bg-sky-100 text-sky-800',
  },
  shopify_tracking_airboss_update_failure: {
    label: 'Shopify Tracking (AirBoss)',
    className: 'bg-indigo-100 text-indigo-800',
  },
};

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function metricPill(metricType?: string) {
  const metric = METRIC_LABELS[metricType ?? ''] ?? {
    label: metricType || 'Unknown',
    className: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${metric.className}`}
    >
      {metric.label}
    </span>
  );
}

function airbossJobUrl(item: TriageItem): string {
  if (item.fulfillmentJobId) {
    return `https://airboss-prod.com/airboss-admin/fulfillment/job/${item.fulfillmentJobId}/change/`;
  }
  const query = item.sourceData.ship_memo_id || item.sourceData.shopify_order_id;
  return `https://airboss-prod.com/airboss-admin/fulfillment/job/?q=${query}`;
}

/** Error payloads are usually JSON — pretty-print when they parse, show verbatim otherwise. */
function formatErrorPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

function FulfillmentSyncRow({ date, item }: { date: string; item: TriageItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const check = useCheckTriageItem(QUEUE, date);
  const update = useUpdateTriageItem(QUEUE, date);

  const busy = check.isPending || update.isPending;
  const resolved = item.status === 'resolved';
  const actionError = check.error instanceof Error ? check.error.message : undefined;
  const errorPayload = item.sourceData.response_payload;

  return (
    <>
      <tr
        onClick={() => setIsExpanded(!isExpanded)}
        className={`hover:bg-gray-50 cursor-pointer ${resolved ? 'bg-gray-50' : ''}`}
      >
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-gray-400">
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="font-mono text-sm text-gray-900">
            {item.sourceData.shopify_order_id || '—'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {metricPill(item.sourceData.metric_type)}
            <span className="text-sm text-gray-600">{item.reason || '—'}</span>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <StatusBadge status={item.status === 'resolved' ? 'resolved' : 'pending'} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-wrap items-center gap-2">
            {!resolved && (
              <button
                onClick={() => check.mutate(item.itemId)}
                disabled={busy}
                className="px-3 py-1 text-sm font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {check.isPending ? 'Checking...' : 'Check Status'}
              </button>
            )}
            <button
              onClick={() =>
                update.mutate({ itemId: item.itemId, status: resolved ? 'pending' : 'resolved' })
              }
              disabled={busy}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                resolved
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {update.isPending ? 'Saving...' : resolved ? 'Reopen' : 'Resolve'}
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-4 py-4">
            <div className="space-y-4">
              {/* Details */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="text-sm">
                    <span className="text-gray-500">Ship Memo:</span>{' '}
                    <span className="text-gray-900 font-mono">
                      {item.sourceData.ship_memo_id || '—'}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Failed At:</span>{' '}
                    <span className="text-gray-900">
                      {formatDateTime(item.sourceData.created_at)}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Shopify Order:</span>{' '}
                    {item.sourceData.shopify_order_id ? (
                      <a
                        href={`https://admin.shopify.com/store/skimsbody/orders/${item.sourceData.shopify_order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.sourceData.shopify_order_id}
                      </a>
                    ) : (
                      <span className="text-gray-400">Not found</span>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">AirBoss Job:</span>{' '}
                    <a
                      href={airbossJobUrl(item)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.fulfillmentJobId ? item.fulfillmentJobId : 'Search jobs'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Error payload */}
              {errorPayload && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Error Payload</h4>
                  <pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-all">
                    {formatErrorPayload(errorPayload)}
                  </pre>
                </div>
              )}

              {/* Check Status error */}
              {actionError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                  <p className="font-medium text-red-800 mb-1">Check failed</p>
                  <p className="text-red-700 font-mono text-xs whitespace-pre-wrap">{actionError}</p>
                </div>
              )}

              {/* Resolution Info */}
              {item.resolvedAt && (
                <div className="text-sm text-gray-600">
                  Resolved on {formatDateTime(item.resolvedAt)}
                  {item.resolvedBy && ` by ${item.resolvedBy}`}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function FulfillmentSyncDetail() {
  const { date } = useParams<{ date: string }>();
  const { data, isLoading, error } = useTriageBatch(QUEUE, date || '');

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          Error loading batch: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Link to="/fulfillment-sync" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
          Back to Fulfillment Sync Failures
        </Link>
      </div>
    );
  }

  const formattedDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const progressPercent =
    data?.batch && data.batch.totalItems > 0
      ? Math.round((data.batch.resolvedItems / data.batch.totalItems) * 100)
      : 0;

  // Pending first, then by id for a stable order
  const items = [...(data?.items ?? [])].sort((a, b) =>
    a.status === b.status ? a.itemId.localeCompare(b.itemId) : a.status === 'pending' ? -1 : 1
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/fulfillment-sync" className="text-gray-400 hover:text-gray-600 transition-colors">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{formattedDate}</h1>
          {data?.batch && (
            <p className="text-sm text-gray-500">
              Imported {formatDateTime(data.batch.importedAt)}
            </p>
          )}
        </div>
        {data?.batch && <StatusBadge status={data.batch.status} />}
      </div>

      {/* Progress */}
      {data?.batch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">
              {data.batch.resolvedItems} / {data.batch.totalItems} failures resolved
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                data.batch.status === 'complete'
                  ? 'bg-green-500'
                  : data.batch.status === 'partial'
                    ? 'bg-blue-500'
                    : 'bg-yellow-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500 text-right">{progressPercent}% complete</p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No sync failures in this batch.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  &nbsp;
                </th>
                {['Order ID', 'Reason', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item) => (
                <FulfillmentSyncRow key={item.itemId} date={date!} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
